/**
 * [T270-IMPL-PREFLIGHT] Trends Redesign Skin — T270-PY (FE-2)
 *
 * Presentational downstream of `TrendsAdapter`. Owns the chip strip
 * (9 chips, AC1), the chart-type popover trigger (`Settings2` icon, AC4), the
 * Area / Bar / Line wrapper switch (AC5), the placeholder series toggles
 * (AC6), the loading / error / empty states (AC8) and the semantic palette
 * via `useDataToken()` (AC9 / plan §4.5).
 *
 * Color contract:
 *   - Income → `useDataToken('positive')` (green family)
 *   - Expenses → `useDataToken('negative')` (red family)
 *   - Net → `useDataToken('neutral')` (blue family)
 *   - NEVER `hsl(var(--chart-*))` (BANNED — binding contract §4.4 / T270 §8.4).
 *   - NEVER `text-chart-*` / `bg-chart-*` / `border-chart-*` Tailwind legacy
 *     tokens. Inline `style={{ ... }}` or Recharts `fill`/`stroke` props only.
 *
 * State machine:
 *   - Chip change → adapter fires a new `buildSpendingOverTime(...)` call
 *     (state-owned by the adapter, NOT by this skin).
 *   - Chart-type change → instant wrapper swap (no fetch — pure visual).
 *   - Series toggle → no-op in T270-PY (placeholder copy until T271-PY).
 *
 * @see /tmp/opencode/T270-IMPL-PREFLIGHT-trends-contract.md
 * @see docs/plans/transaction-insights-trends-redesign.md §3 / §4 / §6
 *
 * @module transactions/TrendsRedesignSkin
 */
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  } from 'recharts';
import {
  AreaChart as AreaChartIcon,
  BarChart3 as BarChartIcon,
  LineChart as LineChartIcon,
  Settings2,
  } from 'lucide-react';

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { IconButton } from '@/components/ui/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

import { INSIGHT_CHART_CONTAINER_CLASS, INSIGHT_CHART_MARGIN } from '@/lib/chartLayout';
import { useDataToken } from '@/lib/design/widgetTokens';
import { useIsMobile } from '@/hooks/use-mobile';
import type { SkinError } from '@/components/dashboard/widgets/skins/types';
import {
  SkinEmptyState,
  SkinErrorState,
  SkinLoadingLine,
} from '@/components/dashboard/widgets/skins/insights/_shared';
import { cn } from '@/lib/utils';
import type { SpendingTrendPayload } from '@/lib/insightsContracts';

import { CHIP_TABLE, type TrendPeriod } from './TrendsAdapter';

// ── Public types ────────────────────────────────────────────────────────────

type ChartType = 'area' | 'bar' | 'line';
type SeriesId = 'income' | 'expenses' | 'net';
type ResolvedGranularity = 'day' | 'week' | 'month';

export interface TrendsRedesignSkinProps {
  /** Selected chip. Mirrors `TrendGranularity` (one of 9 values incl. `'auto'`). */
  selectedPeriod: TrendPeriod;
  /** Active chart wrapper (`<AreaChart>` | `<BarChart>` | `<LineChart>`). */
  chartType: ChartType;
  /** Active series (Income / Expenses / Net). Default all 3. */
  enabledSeries: readonly SeriesId[];
  /** Source data — built locally in the adapter from `buildSpendingOverTime`. */
  data: SpendingTrendPayload;
  loading: boolean;
  error?: SkinError;
  /** Workbench-echoed granularity used for the Auto chip suffix (AC3). */
  resolvedGranularity: ResolvedGranularity;
  onPeriodChange: (p: TrendPeriod) => void;
  onChartTypeChange: (t: ChartType) => void;
  onToggleSeries: (s: SeriesId) => void;
}

// ── Private constants ───────────────────────────────────────────────────────


const SERIES_LABEL: Record<SeriesId, string> = {
  income: 'Income',
  expenses: 'Expenses',
  net: 'Net',
};

const CHART_TYPE_LABEL: Record<ChartType, string> = {
  area: 'Area',
  bar: 'Bar',
  line: 'Line',
};

// ChartContainer needs a serialisable `ChartConfig` for the legend. We supply
// minimal entries (label + color placeholder) — actual stroke + fill are
// pushed through Recharts props so the dynamic `useDataToken` resolution
// reaches Recharts without a CSS variable re-evaluation race.
const LEGEND_CHART_CONFIG: ChartConfig = {
  income: { label: 'Income', color: '' },
  expenses: { label: 'Expenses', color: '' },
  net: { label: 'Net', color: '' },
};

const RESOLVED_GRANULARITY_SUFFIX: Record<ResolvedGranularity, 'DAY' | 'WEEK' | 'MONTH'> = {
  day: 'DAY',
  week: 'WEEK',
  month: 'MONTH',
};

// ── Component ───────────────────────────────────────────────────────────────

export function TrendsRedesignSkin({
  selectedPeriod,
  chartType,
  enabledSeries,
  data,
  loading,
  error,
  resolvedGranularity,
  onPeriodChange,
  onChartTypeChange,
  onToggleSeries,
}: TrendsRedesignSkinProps) {
  // Resolve the 3 series colours via `useDataToken` — NEVER legacy chart cycle.
  const incomeColor = useDataToken('positive');
  const expensesColor = useDataToken('negative');
  const netColor = useDataToken('neutral');

  const colors = useMemo(
    () => ({ income: incomeColor, expenses: expensesColor, net: netColor }),
    [incomeColor, expensesColor, netColor],
  );

  const enabledSeriesSet = useMemo(() => new Set(enabledSeries), [enabledSeries]);
  const months = data?.months ?? [];
  const isEmpty = !loading && !error && months.length === 0;

  // ── Loading state (AC8) ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        data-testid="trends-redesign-loading"
        role="status"
        aria-live="polite"
        className="space-y-3"
      >
        <Skeleton className="h-10 w-full rounded-md" />
        <SkinLoadingLine />
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        data-testid="trends-redesign-error"
        role="status"
        aria-live="polite"
        className="w-full min-w-0"
      >
        <SkinErrorState message={error.message} />
        <div className="flex justify-center">
          <Button
            data-testid="trends-redesign-retry"
            onClick={() => window.location.reload()}
            size="sm"
            variant="outline"
            className="mt-2 min-h-[44px]"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ── Empty state ─────────────────────────────────────────────────────────
  if (isEmpty) {
    return (
      <div
        data-testid="trends-redesign-empty"
        role="status"
        aria-live="polite"
        className="w-full min-w-0"
      >
        <SkinEmptyState
          title="No trend data available"
          description="Add transactions over time to see your income, expenses, and net trends."
          cta={{ label: 'Add transaction', to: '/transactions' }}
        />
      </div>
    );
  }

  return (
    <div
      data-testid="trends-redesign-root"
      className="space-y-3 w-full min-w-0"
    >
      {/* ── Header row: chip strip + chart-type trigger ──────────────── */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <ChipStrip
          selectedPeriod={selectedPeriod}
          resolvedGranularitySuffix={
            selectedPeriod === 'auto'
              ? RESOLVED_GRANULARITY_SUFFIX[resolvedGranularity]
              : null
          }
          onPeriodChange={onPeriodChange}
        />
        <ChartSettingsTrigger
          chartType={chartType}
          enabledSeries={enabledSeries}
          onChartTypeChange={onChartTypeChange}
          onToggleSeries={onToggleSeries}
        />
      </div>

      {/* ── Chart area (AC5: identical axes/tooltip/legend across wrappers) */}
      <div
        role="img"
        aria-label={`Spending trends: ${months.length} ${months.length === 1 ? 'data point' : 'data points'}`}
        className="w-full min-w-0"
        data-testid="trends-redesign-chart"
      >
        <ChartContainer config={LEGEND_CHART_CONFIG} className={INSIGHT_CHART_CONTAINER_CLASS}>
          <ChartRenderer
            chartType={chartType}
            months={months}
            enabledSeriesSet={enabledSeriesSet}
            colors={colors}
          />
        </ChartContainer>
      </div>
    </div>
  );
}

// ── Sub: chip strip (9 chips, dual-mode desktop / mobile) ───────────────────

interface ChipStripProps {
  selectedPeriod: TrendPeriod;
  /**
   * When `selectedPeriod === 'auto'`, the chip emits `Auto (<suffix>)` where
   * `<suffix>` matches the workbench-resolved backend granularity (`DAY` /
   * `WEEK` / `MONTH`). When `selectedPeriod !== 'auto'`, the prop is `null`
   * and the chip emits its literal label (e.g. `'1M'`). Source: binding
   * contract §2.2 / AC3.
   */
  resolvedGranularitySuffix: 'DAY' | 'WEEK' | 'MONTH' | null;
  onPeriodChange: (p: TrendPeriod) => void;
}

function ChipStrip({
  selectedPeriod,
  resolvedGranularitySuffix,
  onPeriodChange,
}: ChipStripProps) {
  // Bind the suffix ONCE per render — the chip is mutually-exclusive single
  // select, so only the active chip's `data-active` style matters.
  return (
    <div
      role="tablist"
      aria-label="Trends period"
      className={cn(
        // [T270-PY-REV2] Mobile-safe scroll pills (<640 px); desktop retains
        // the inline segmented control (≥640 px). `hidden` was removed so all
        // 9 chips render on mobile; `overflow-x-auto max-w-full` keeps the
        // strip within the parent flex item and introduces horizontal scroll
        // ONLY inside the chip strip (never on chart/data content).
        // `justify-start sm:justify-center` left-aligns on mobile for a scroll
        // affordance and re-centres the segmented control on desktop. Semantic
        // tokens (`bg-muted`, `text-muted-foreground`) and per-chip
        // `min-h-[44px] min-w-[44px]` touch targets are preserved.
        'inline-flex h-10 items-center justify-start sm:justify-center rounded-md bg-muted p-1 text-muted-foreground overflow-x-auto sm:overflow-visible max-w-full',
      )}
      data-testid="trends-redesign-granularity-strip"
    >
      {CHIP_TABLE.map((chip) => {
        const selected = selectedPeriod === chip.period;
        const isAuto = chip.period === 'auto';
        return (
          <button
            key={chip.period}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={
              chip.days === null
                ? `${chip.label}, auto-resolved`
                : `${chip.label}, ${chip.days} days`
            }
            data-testid={`trends-redesign-chip-${chip.label}`}
            data-active={selected || undefined}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 min-h-[44px] min-w-[44px] text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              selected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => onPeriodChange(chip.period)}
          >
            {chip.label}
            {isAuto && resolvedGranularitySuffix ? (
              <span className="text-muted-foreground ml-1">
                ({resolvedGranularitySuffix})
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ── Sub: chart-type trigger button + Popover/Sheet container ────────────────

interface ChartSettingsTriggerProps {
  chartType: ChartType;
  enabledSeries: readonly SeriesId[];
  onChartTypeChange: (t: ChartType) => void;
  onToggleSeries: (s: SeriesId) => void;
}

function ChartSettingsTrigger({
  chartType,
  enabledSeries,
  onChartTypeChange,
  onToggleSeries,
}: ChartSettingsTriggerProps) {
  const isMobile = useIsMobile();
  const enabledSet = useMemo(() => new Set(enabledSeries), [enabledSeries]);

  // The trigger is identical across desktop (Popover) and mobile (Sheet).
  // Only the wrapper differs. Mirror of `ChartConfigPopover.tsx:96-122`.
  const trigger = (
    <IconButton
      variant="ghost"
      size="icon"
      aria-label="Chart settings"
      tooltip="Chart settings"
      data-testid="trends-redesign-chart-type-menu"
      className="min-h-[44px] min-w-[44px]"
    >
      <Settings2 className="h-4 w-4" />
    </IconButton>
  );

  const panel = (
    <ChartSettingsPanel
      chartType={chartType}
      enabledSeriesSet={enabledSet}
      onChartTypeChange={onChartTypeChange}
      onToggleSeries={onToggleSeries}
    />
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[70vh] overflow-y-auto"
          data-testid="trends-redesign-chart-type-sheet"
        >
          <SheetHeader>
            <SheetTitle>Chart settings</SheetTitle>
          </SheetHeader>
          <div className="mt-4">{panel}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[300px]"
        data-testid="trends-redesign-chart-type-panel"
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}

interface ChartSettingsPanelProps {
  chartType: ChartType;
  enabledSeriesSet: Set<SeriesId>;
  onChartTypeChange: (t: ChartType) => void;
  onToggleSeries: (s: SeriesId) => void;
}

function ChartSettingsPanel({
  chartType,
  enabledSeriesSet,
  onChartTypeChange,
  onToggleSeries,
}: ChartSettingsPanelProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Series toggles — AC6 placeholder for T271-PY. The binding contract
          §10 explicitly defers multi-series switching to a future epic;
          T270-PY disables all three rows so the chip is present but inert. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          Series (3/3 active in T270-PY)
        </legend>
        {(['income', 'expenses', 'net'] as const).map((id) => (
          <SeriesRow
            key={id}
            id={id}
            label={SERIES_LABEL[id]}
            enabled={enabledSeriesSet.has(id)}
            onToggle={() => onToggleSeries(id)}
          />
        ))}
        <p
          className="text-xs text-muted-foreground"
          data-testid="trends-redesign-series-coming-soon"
        >
          Multi-series toggles coming in T271-PY.
        </p>
      </fieldset>

      {/* Chart type picker — AC4 / AC5. Wrapper component (AreaChart /
          BarChart / LineChart) is chosen in the parent `<ChartRenderer>`
          via the `chartType` prop. Here we only collect user intent. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Chart type</legend>
        <div className="flex gap-1.5">
          {(['area', 'bar', 'line'] as const).map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`trends-redesign-chart-type-${t === 'area' ? 'Area' : t === 'bar' ? 'Bar' : 'Line'}`}
              aria-pressed={chartType === t}
              aria-label={`${CHART_TYPE_LABEL[t]} chart`}
              onClick={() => onChartTypeChange(t)}
              className={cn(
                'flex-1 gap-1.5 min-h-[44px] inline-flex items-center justify-center rounded-md text-sm font-medium',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                chartType === t
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-input bg-background hover:bg-accent',
              )}
            >
              {t === 'area' ? (
                <AreaChartIcon className="h-4 w-4" />
              ) : t === 'bar' ? (
                <BarChartIcon className="h-4 w-4" />
              ) : (
                <LineChartIcon className="h-4 w-4" />
              )}
              <span className="text-xs">{CHART_TYPE_LABEL[t]}</span>
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

interface SeriesRowProps {
  id: SeriesId;
  label: string;
  enabled: boolean;
  onToggle: () => void;
}

function SeriesRow({ id, label, enabled, onToggle }: SeriesRowProps) {
  return (
    <div
      className="flex items-center gap-3 min-h-[44px]"
      data-testid={`trends-redesign-series-toggle-${id}`}
    >
      <Switch
        id={`trends-redesign-series-${id}`}
        checked={enabled}
        disabled
        // T270-PY: all three series render in the chart, so the switch is
        // disabled at the UI level (contract §6 row "All three disabled in
        // T270-PY with 'Coming in T271-PY' tooltip"). The handler is wired
        // but never fires until T271-PY extends the workbench.
        onCheckedChange={onToggle}
      />
      <Label
        htmlFor={`trends-redesign-series-${id}`}
        className="flex-1 text-sm text-muted-foreground cursor-not-allowed"
        title="Coming in T271-PY"
      >
        {label}
      </Label>
    </div>
  );
}

// ── Sub: chart wrapper switch (Area / Bar / Line — identical axes + tooltip) ─

/**
 * [T271-PY] Width/height are injected by Recharts `ResponsiveContainer` via
 * `React.cloneElement(child, { width, height })` (see
 * `node_modules/recharts/es6/component/ResponsiveContainer.js:130-145`). They are
 * required by the chart wrappers (`<AreaChart>`/`<BarChart>`/`<LineChart>`) — without
 * them, the rendered SVG has 0×0 dimensions and renders nothing. The intermediate
 * `<ChartRenderer>` wrapper here previously DROPPED the cloned `width`/`height` props
 * (its function signature did not declare them), which produced the T271-PY INCIDENT:
 * blank chart area on the Trends tab even though `recharts-responsive-container`
 * mounted with the correct measured size (1070 × 601.875). `Restored the type by
 * re-adding the interface (the previous in-place edit consumed it)` + fowarding them
 * to the chart wrappers below.
 */
interface ChartRendererProps {
  chartType: ChartType;
  months: SpendingTrendPayload['months'];
  enabledSeriesSet: Set<SeriesId>;
  colors: Record<SeriesId, string>;
  /** Injected by `<RechartsPrimitive.ResponsiveContainer>` cloneElement — required by chart wrappers. */
  width?: number;
  /** Injected by `<RechartsPrimitive.ResponsiveContainer>` cloneElement — required by chart wrappers. */
  height?: number;
}

function ChartRenderer({
  chartType,
  months,
  enabledSeriesSet,
  colors,
  width,
  height,
}: ChartRendererProps) {
  // Identical data + axes + tooltip + legend per AC5 — only the wrapper + the
  // series element differs. The wrapper component is chosen up front so each
  // branch can use its own series element (`<Area>` vs `<Bar>` vs `<Line>`).
  return (
    <>
      {chartType === 'area' && (
        <AreaChart data={months} margin={INSIGHT_CHART_MARGIN} width={width} height={height}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={compactCurrency}
            tickLine={false}
            axisLine={false}
            width={36}
            tick={{ fontSize: 10 }}
          />
          <ChartTooltip content={<ChartTooltipContent labelKey="month" />} />
          {enabledSeriesSet.has('income') && (
            <Area
              type="monotone"
              dataKey="income"
              stroke={colors.income}
              fill={colors.income}
              fillOpacity={0.3}
            />
          )}
          {enabledSeriesSet.has('expenses') && (
            <Area
              type="monotone"
              dataKey="expenses"
              stroke={colors.expenses}
              fill={colors.expenses}
              fillOpacity={0.3}
            />
          )}
          {enabledSeriesSet.has('net') && (
            <Area
              type="monotone"
              dataKey="net"
              stroke={colors.net}
              fill={colors.net}
              fillOpacity={0.15}
            />
          )}
          <ChartLegend content={<ChartLegendContent />} />
        </AreaChart>
      )}

      {chartType === 'bar' && (
        <BarChart data={months} margin={INSIGHT_CHART_MARGIN} width={width} height={height}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={compactCurrency}
            tickLine={false}
            axisLine={false}
            width={36}
            tick={{ fontSize: 10 }}
          />
          <ChartTooltip content={<ChartTooltipContent labelKey="month" />} />
          {enabledSeriesSet.has('income') && (
            <Bar dataKey="income" fill={colors.income} radius={[2, 2, 0, 0]} />
          )}
          {enabledSeriesSet.has('expenses') && (
            <Bar dataKey="expenses" fill={colors.expenses} radius={[2, 2, 0, 0]} />
          )}
          {enabledSeriesSet.has('net') && (
            <Bar dataKey="net" fill={colors.net} radius={[2, 2, 0, 0]} />
          )}
          <ChartLegend content={<ChartLegendContent />} />
        </BarChart>
      )}

      {chartType === 'line' && (
        <LineChart data={months} margin={INSIGHT_CHART_MARGIN} width={width} height={height}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={compactCurrency}
            tickLine={false}
            axisLine={false}
            width={36}
            tick={{ fontSize: 10 }}
          />
          <ChartTooltip content={<ChartTooltipContent labelKey="month" />} />
          {enabledSeriesSet.has('income') && (
            <Line
              type="monotone"
              dataKey="income"
              stroke={colors.income}
              strokeWidth={2}
              dot={false}
            />
          )}
          {enabledSeriesSet.has('expenses') && (
            <Line
              type="monotone"
              dataKey="expenses"
              stroke={colors.expenses}
              strokeWidth={2}
              dot={false}
            />
          )}
          {enabledSeriesSet.has('net') && (
            <Line
              type="monotone"
              dataKey="net"
              stroke={colors.net}
              strokeWidth={2}
              dot={false}
            />
          )}
          <ChartLegend content={<ChartLegendContent />} />
        </LineChart>
      )}
    </>
  );
}


// ── Helpers ─────────────────────────────────────────────────────────────────

function compactCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return String(value);
}


export default TrendsRedesignSkin;
